import React from "react";
import { Link } from "react-router-dom";
import { MapPin, Truck, Phone } from "lucide-react";

interface Department {
  code: string;
  name: string;
}

interface City {
  name: string;
  code: string;
  dept: string;
}

interface ServiceZonesProps {
  departements: Department[];
  majorCities: City[];
}

const ServiceZones: React.FC<ServiceZonesProps> = ({ departements, majorCities }) => {
  // Départements avec pages SEO dédiées (maintenant étendues)
  const departmentsWithSEOPages = ["12", "31", "81", "46", "48", "34", "82", "95"];
  
  return (
    <section className="mt-16 bg-white rounded-lg shadow-lg p-8" id="service-zones">
      <header className="text-center mb-8">
        <h2 className="text-3xl font-bold text-fabre-gray mb-4">
          Nos zones de service - Location de bennes partout en France
        </h2>
        <p className="text-lg text-gray-600 max-w-4xl mx-auto">
          <strong>Groupe Fabre Environnement</strong> propose ses services de location de bennes dans 
          <strong> 8 départements français</strong>. Découvrez nos zones d'intervention et trouvez 
          le service de location de bennes le plus proche de chez vous avec 
          <strong> livraison rapide en 24-48h</strong>.
        </p>
      </header>
      
      {/* Section départements avec SEO renforcé */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-12">
        <div className="space-y-6">
          <div className="flex items-center gap-3 mb-6">
            <MapPin className="h-6 w-6 text-fabre-green" />
            <h3 className="text-2xl font-bold text-fabre-gray">
              Location de bennes par département
            </h3>
          </div>
          <p className="text-gray-600 mb-6">
            Nos services de location de bennes couvrent 8 départements avec des équipes locales 
            expertes. Chaque département bénéficie d'un service personnalisé adapté aux 
            spécificités territoriales et réglementaires locales.
          </p>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {departements.map(dept => (
              <div key={dept.code} className="bg-gray-50 rounded-lg p-4 hover:bg-fabre-lightgreen transition-colors group">
                <Link 
                  to={`/nos-services/location-bennes/${dept.code}`}
                  className="block"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Truck className="h-5 w-5 text-fabre-green" />
                    <h4 className="font-semibold text-fabre-darkgreen group-hover:text-fabre-green transition-colors">
                      {dept.name} ({dept.code})
                    </h4>
                  </div>
                  <p className="text-sm text-gray-600 mb-2">
                    Service complet de location de bennes dans le {dept.name}
                  </p>
                  <span className="text-sm text-fabre-green group-hover:underline">
                    Voir nos services {dept.name} →
                  </span>
                </Link>
              </div>
            ))}
          </div>

          {/* Section spéciale pour les départements avec pages SEO avancées */}
          <div className="mt-8 p-6 bg-gradient-to-r from-fabre-lightgreen to-white rounded-lg">
            <h4 className="text-lg font-semibold text-fabre-darkgreen mb-4">
              Nos zones d'expertise prioritaires
            </h4>
            <div className="grid grid-cols-1 gap-3">
              <Link 
                to="/nos-services/location-bennes/12"
                className="group bg-white p-4 rounded-lg shadow hover:shadow-md transition-all"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h5 className="font-semibold text-fabre-darkgreen group-hover:text-fabre-green">
                      🏢 Aveyron (12) - Siège social
                    </h5>
                    <p className="text-sm text-gray-600">Rodez, Millau, Villefranche - Service premium</p>
                  </div>
                  <span className="text-fabre-green text-sm">Découvrir →</span>
                </div>
              </Link>
              
              <Link 
                to="/nos-services/location-bennes/31"
                className="group bg-white p-4 rounded-lg shadow hover:shadow-md transition-all"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h5 className="font-semibold text-fabre-darkgreen group-hover:text-fabre-green">
                      🏙️ Haute-Garonne (31) - Métropole
                    </h5>
                    <p className="text-sm text-gray-600">Toulouse, Muret - Solutions urbaines</p>
                  </div>
                  <span className="text-fabre-green text-sm">Découvrir →</span>
                </div>
              </Link>

              <Link 
                to="/nos-services/location-bennes/81"
                className="group bg-white p-4 rounded-lg shadow hover:shadow-md transition-all"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h5 className="font-semibold text-fabre-darkgreen group-hover:text-fabre-green">
                      🏛️ Tarn (81) - Patrimoine UNESCO
                    </h5>
                    <p className="text-sm text-gray-600">Albi, Castres, Mazamet - Solutions patrimoniales</p>
                  </div>
                  <span className="text-fabre-green text-sm">Découvrir →</span>
                </div>
              </Link>

              <Link 
                to="/nos-services/location-bennes/46"
                className="group bg-white p-4 rounded-lg shadow hover:shadow-md transition-all"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h5 className="font-semibold text-fabre-darkgreen group-hover:text-fabre-green">
                      🌿 Lot (46) - Sites protégés
                    </h5>
                    <p className="text-sm text-gray-600">Cahors, Figeac, Gourdon - Solutions écologiques</p>
                  </div>
                  <span className="text-fabre-green text-sm">Découvrir →</span>
                </div>
              </Link>

              <Link 
                to="/nos-services/location-bennes/82"
                className="group bg-white p-4 rounded-lg shadow hover:shadow-md transition-all"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h5 className="font-semibold text-fabre-darkgreen group-hover:text-fabre-green">
                      🚜 Tarn-et-Garonne (82) - Agroalimentaire
                    </h5>
                    <p className="text-sm text-gray-600">Montauban, Moissac - Solutions agricoles</p>
                  </div>
                  <span className="text-fabre-green text-sm">Découvrir →</span>
                </div>
              </Link>

              <Link 
                to="/nos-services/location-bennes/15"
                className="group bg-white p-4 rounded-lg shadow hover:shadow-md transition-all"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h5 className="font-semibold text-fabre-darkgreen group-hover:text-fabre-green">
                      🏔️ Cantal (15) - Montagne
                    </h5>
                    <p className="text-sm text-gray-600">Aurillac, Saint-Flour - Solutions haute altitude</p>
                  </div>
                  <span className="text-fabre-green text-sm">Contact →</span>
                </div>
              </Link>

              <Link 
                to="/nos-services/location-bennes/48"
                className="group bg-white p-4 rounded-lg shadow hover:shadow-md transition-all"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h5 className="font-semibold text-fabre-darkgreen group-hover:text-fabre-green">
                      ⛰️ Lozère (48) - Haute montagne
                    </h5>
                    <p className="text-sm text-gray-600">Mende, Florac - Solutions contraintes d'accès</p>
                  </div>
                  <span className="text-fabre-green text-sm">Découvrir →</span>
                </div>
              </Link>

              <Link 
                to="/nos-services/location-bennes/34"
                className="group bg-white p-4 rounded-lg shadow hover:shadow-md transition-all"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h5 className="font-semibold text-fabre-darkgreen group-hover:text-fabre-green">
                      🏖️ Hérault (34) - Littoral
                    </h5>
                    <p className="text-sm text-gray-600">Montpellier, Béziers, Sète - Solutions côtières</p>
                  </div>
                  <span className="text-fabre-green text-sm">Découvrir →</span>
                </div>
              </Link>

              <Link 
                to="/nos-services/location-bennes/95"
                className="group bg-white p-4 rounded-lg shadow hover:shadow-md transition-all"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h5 className="font-semibold text-fabre-darkgreen group-hover:text-fabre-green">
                      🏗️ Val-d'Oise (95) - Île-de-France
                    </h5>
                    <p className="text-sm text-gray-600">Cergy, Argenteuil - Solutions franciliennes</p>
                  </div>
                  <span className="text-fabre-green text-sm">Découvrir →</span>
                </div>
              </Link>
            </div>
          </div>
        </div>
        
        {/* Section villes principales avec plus de détails SEO */}
        <div className="space-y-6">
          <div className="flex items-center gap-3 mb-6">
            <MapPin className="h-6 w-6 text-fabre-green" />
            <h3 className="text-2xl font-bold text-fabre-gray">
              Location de bennes par ville
            </h3>
          </div>
          <p className="text-gray-600 mb-6">
            Intervention dans les principales villes et agglomérations. 
            Notre réseau local nous permet d'assurer une <strong>livraison rapide</strong> et 
            un <strong>service de proximité</strong> dans chaque zone urbaine.
          </p>
          
          <div className="space-y-3">
            {majorCities.slice(0, 8).map(city => (
              <div key={city.code} className="bg-gray-50 rounded-lg p-3 hover:bg-fabre-lightgreen transition-colors group">
                <Link 
                  to={departmentsWithSEOPages.includes(city.dept) 
                    ? `/nos-services/location-bennes/${city.dept}` 
                    : `/nos-services/location-bennes?ville=${city.name.toLowerCase()}&cp=${city.code}`
                  }
                  className="flex items-center justify-between"
                >
                  <div>
                    <h4 className="font-medium text-fabre-darkgreen group-hover:text-fabre-green transition-colors">
                      Location benne {city.name}
                      {departmentsWithSEOPages.includes(city.dept) && (
                        <span className="ml-2 text-xs bg-fabre-green text-white px-2 py-1 rounded">★ Premium</span>
                      )}
                    </h4>
                    <p className="text-sm text-gray-600">
                      Code postal {city.code} - Département {city.dept}
                    </p>
                  </div>
                  <span className="text-fabre-green text-sm group-hover:underline">
                    {departmentsWithSEOPages.includes(city.dept) ? "Voir détails" : "Devis"} →
                  </span>
                </Link>
              </div>
            ))}
          </div>
          
          <div className="bg-fabre-lightgreen p-4 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Phone className="h-5 w-5 text-fabre-darkgreen" />
              <h4 className="font-semibold text-fabre-darkgreen">
                Votre ville n'apparaît pas ?
              </h4>
            </div>
            <p className="text-sm text-gray-700 mb-3">
              Nous étudions toute demande d'intervention dans de nouvelles zones. 
              Contactez-nous pour vérifier la faisabilité dans votre secteur.
            </p>
            <a 
              href="tel:+33565787900"
              className="inline-flex items-center text-fabre-green hover:text-fabre-darkgreen font-medium text-sm"
            >
              <Phone className="h-4 w-4 mr-1" />
              05 65 78 79 00
            </a>
          </div>
        </div>
      </div>
      
      {/* Section services populaires avec longue traîne SEO */}
      <div className="border-t border-gray-200 pt-8">
        <h3 className="text-2xl font-bold text-fabre-gray mb-6 text-center">
          Recherches populaires - Location de bennes par zone
        </h3>
        <p className="text-center text-gray-600 mb-8 max-w-3xl mx-auto">
          Découvrez nos services les plus demandés par région et par type de benne. 
          Chaque service est adapté aux besoins locaux avec des tarifs transparents.
        </p>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <Link 
            to="/nos-services/location-bennes/12" 
            className="group bg-gradient-to-br from-gray-50 to-fabre-lightgreen p-4 rounded-lg hover:shadow-md transition-all"
          >
            <h4 className="font-semibold text-fabre-darkgreen group-hover:text-fabre-green mb-2">
              🏢 Location benne Aveyron (12)
            </h4>
            <p className="text-sm text-gray-600">
              Service premium à Rodez, Millau, Villefranche-de-Rouergue
            </p>
            <div className="mt-2 text-xs text-fabre-green font-medium">Page dédiée →</div>
          </Link>
          
          <Link 
            to="/nos-services/location-bennes/31" 
            className="group bg-gradient-to-br from-gray-50 to-fabre-lightgreen p-4 rounded-lg hover:shadow-md transition-all"
          >
            <h4 className="font-semibold text-fabre-darkgreen group-hover:text-fabre-green mb-2">
              🏙️ Location benne Toulouse (31)
            </h4>
            <p className="text-sm text-gray-600">
              Solutions métropole toulousaine et Haute-Garonne
            </p>
            <div className="mt-2 text-xs text-fabre-green font-medium">Page dédiée →</div>
          </Link>
          
          <Link 
            to="/nos-services/location-bennes/81" 
            className="group bg-gradient-to-br from-gray-50 to-fabre-lightgreen p-4 rounded-lg hover:shadow-md transition-all"
          >
            <h4 className="font-semibold text-fabre-darkgreen group-hover:text-fabre-green mb-2">
              🏛️ Location benne Tarn (81)
            </h4>
            <p className="text-sm text-gray-600">
              Albi UNESCO, Castres, Mazamet et communes
            </p>
            <div className="mt-2 text-xs text-fabre-green font-medium">Page dédiée →</div>
          </Link>
          
          <Link 
            to="/nos-services/location-bennes/46" 
            className="group bg-gradient-to-br from-gray-50 to-fabre-lightgreen p-4 rounded-lg hover:shadow-md transition-all"
          >
            <h4 className="font-semibold text-fabre-darkgreen group-hover:text-fabre-green mb-2">
              🌿 Location benne Lot (46)
            </h4>
            <p className="text-sm text-gray-600">
              Cahors, Figeac, Gourdon et sites protégés
            </p>
            <div className="mt-2 text-xs text-fabre-green font-medium">Page dédiée →</div>
          </Link>
          
          <Link 
            to="/nos-services/location-bennes/82" 
            className="group bg-gradient-to-br from-gray-50 to-fabre-lightgreen p-4 rounded-lg hover:shadow-md transition-all"
          >
            <h4 className="font-semibold text-fabre-darkgreen group-hover:text-fabre-green mb-2">
              🚜 Location benne Tarn-et-Garonne (82)
            </h4>
            <p className="text-sm text-gray-600">
              Montauban, Moissac et solutions agroalimentaires
            </p>
            <div className="mt-2 text-xs text-fabre-green font-medium">Page dédiée →</div>
          </Link>
          
          <Link 
            to="/nos-services/location-bennes/34" 
            className="group bg-gradient-to-br from-gray-50 to-fabre-lightgreen p-4 rounded-lg hover:shadow-md transition-all"
          >
            <h4 className="font-semibold text-fabre-darkgreen group-hover:text-fabre-green mb-2">
              🏖️ Location benne Hérault (34)
            </h4>
            <p className="text-sm text-gray-600">
              Montpellier, Béziers, Sète et littoral méditerranéen
            </p>
            <div className="mt-2 text-xs text-fabre-green font-medium">Page dédiée →</div>
          </Link>
          
          <Link 
            to="/nos-services/location-bennes/48" 
            className="group bg-gradient-to-br from-gray-50 to-fabre-lightgreen p-4 rounded-lg hover:shadow-md transition-all"
          >
            <h4 className="font-semibold text-fabre-darkgreen group-hover:text-fabre-green mb-2">
              ⛰️ Location benne Lozère (48)
            </h4>
            <p className="text-sm text-gray-600">
              Mende, Florac, service haute montagne
            </p>
            <div className="mt-2 text-xs text-fabre-green font-medium">Page dédiée →</div>
          </Link>
          
          <Link 
            to="/nos-services/location-bennes/95" 
            className="group bg-gradient-to-br from-gray-50 to-fabre-lightgreen p-4 rounded-lg hover:shadow-md transition-all"
          >
            <h4 className="font-semibold text-fabre-darkgreen group-hover:text-fabre-green mb-2">
              🏗️ Location benne Val-d'Oise (95)
            </h4>
            <p className="text-sm text-gray-600">
              Cergy, Argenteuil, solutions Île-de-France
            </p>
            <div className="mt-2 text-xs text-fabre-green font-medium">Page dédiée →</div>
          </Link>

          <Link 
            to="/nos-services/location-bennes/15" 
            className="group bg-gradient-to-br from-gray-50 to-fabre-lightgreen p-4 rounded-lg hover:shadow-md transition-all"
          >
            <h4 className="font-semibold text-fabre-darkgreen group-hover:text-fabre-green mb-2">
              🏔️ Location benne Cantal (15)
            </h4>
            <p className="text-sm text-gray-600">
              Aurillac, Saint-Flour et solutions montagne
            </p>
            <div className="mt-2 text-xs text-fabre-green font-medium">Contact →</div>
          </Link>
        </div>
      </div>
      
      {/* Section avantages territoriaux */}
      <div className="mt-12 bg-gradient-to-r from-fabre-lightgreen to-white p-6 rounded-lg">
        <h3 className="text-xl font-bold text-fabre-darkgreen mb-4">
          Pourquoi choisir notre service de proximité ?
        </h3>
        <div className="grid md:grid-cols-3 gap-6">
          <div className="text-center">
            <Truck className="h-8 w-8 text-fabre-green mx-auto mb-2" />
            <h4 className="font-semibold text-fabre-darkgreen mb-2">Livraison locale rapide</h4>
            <p className="text-sm text-gray-600">
              Intervention sous 24-48h grâce à notre réseau de proximité
            </p>
          </div>
          <div className="text-center">
            <MapPin className="h-8 w-8 text-fabre-green mx-auto mb-2" />
            <h4 className="font-semibold text-fabre-darkgreen mb-2">Connaissance du terrain</h4>
            <p className="text-sm text-gray-600">
              Expertise locale des contraintes et réglementations
            </p>
          </div>
          <div className="text-center">
            <Phone className="h-8 w-8 text-fabre-green mx-auto mb-2" />
            <h4 className="font-semibold text-fabre-darkgreen mb-2">Service personnalisé</h4>
            <p className="text-sm text-gray-600">
              Conseils adaptés à votre projet et votre région
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServiceZones;
